import { useState, useMemo, useCallback, useRef, useEffect, memo } from 'react';
import { useRouter } from '@/hooks/use-routing';
import { toast } from 'sonner';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  Input,
  Tooltip,
} from '@sharpe/ui/catalyst';
import { AddLarge, ChevronDown, ArrowLeft, LogoJupyter, Launch } from '@carbon/icons-react';
import { Cardboard } from '@carbon/pictograms-react';
import cn from 'classnames';
import Link from '@/components/smart-link';
import { useCurrentUser } from '@/hooks/use-current-user-org';
import { LoadingSpinner } from '@sharpe/ui/components';
import { formatRelativeTime } from '@sharpe/shared/format';
import { useWorkspaces, type WorkspaceWithChats } from '@/components/use-workspaces';
import { useFavoriteWorkspaces } from '@/hooks/use-favorite-workspaces';
import { useActiveChats } from '@/hooks/use-active-chats';
import { SidebarWorkspaceItem } from './SidebarWorkspaceItem';
import { useSidebarData } from './use-sidebar';
import { useCreateEmptyWorkspace, useCreateChatInWorkspace } from './hooks';
import { useOpenJupyter } from '@/hooks/use-open-jupyter';

// ============================================================================
// TYPES
// ============================================================================

type TimeGroup = 'Today' | 'Yesterday' | 'Last week' | 'Last month' | 'Earlier';

// ============================================================================
// UTILITIES
// ============================================================================

function getTimeGroup(date: Date): TimeGroup {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysDiff = Math.floor((today.getTime() - dateAtMidnight.getTime()) / (1000 * 60 * 60 * 24));

  if (daysDiff === 0) return 'Today';
  if (daysDiff === 1) return 'Yesterday';
  if (daysDiff <= 7) return 'Last week';
  if (daysDiff <= 30) return 'Last month';
  return 'Earlier';
}

function groupWorkspacesByTime(
  workspaces: WorkspaceWithChats[]
): Map<TimeGroup, WorkspaceWithChats[]> {
  const groups = new Map<TimeGroup, WorkspaceWithChats[]>();
  const timeGroups: TimeGroup[] = ['Today', 'Yesterday', 'Last week', 'Last month', 'Earlier'];

  for (const group of timeGroups) {
    groups.set(group, []);
  }

  for (const workspace of workspaces) {
    const group = getTimeGroup(workspace.updatedAt);
    groups.get(group)?.push(workspace);
  }

  return groups;
}

// ============================================================================
// COMPONENTS
// ============================================================================

interface SectionHeaderProps {
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

function SectionHeader({ children, action, className }: SectionHeaderProps) {
  return (
    <div className={cn('flex items-center justify-between px-4 py-2', className)}>
      <span className="text-label-md font-medium text-muted-foreground/50 uppercase tracking-wider">
        {children}
      </span>
      {action}
    </div>
  );
}

interface TimeGroupHeaderProps {
  children: React.ReactNode;
}

function TimeGroupHeader({ children }: TimeGroupHeaderProps) {
  return (
    <div className="sticky top-0 bg-sidebar px-4 py-1.5">
      <span className="text-label-md text-muted-foreground/40">{children}</span>
    </div>
  );
}

interface FocusedWorkspaceViewProps {
  workspace: WorkspaceWithChats;
  activeChatId: string | null;
}

function FocusedWorkspaceView({ workspace, activeChatId }: FocusedWorkspaceViewProps) {
  const { openJupyter, isLoading: isJupyterLoading } = useOpenJupyter();

  const sortedChats = useMemo(
    () =>
      [...workspace.chats].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [workspace.chats]
  );

  const handleOpenJupyter = useCallback(() => {
    openJupyter(workspace.workspaceDir);
  }, [openJupyter, workspace.workspaceDir]);

  const actionBtnClass =
    'w-full flex items-center justify-center gap-1.5 h-[34px] text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Workspace header */}
      <div className="mt-3 mb-1.5">
        <div className="text-[13px] text-foreground/70 truncate px-2 h-[35px] flex items-center border-t border-b border-border">
          {workspace.title || 'Untitled'}
        </div>
        <div className="flex items-center border-b border-border overflow-hidden">
          <div className="flex-1 flex [&>span]:w-full">
            <Tooltip content="Back to all workspaces">
              <Link href="/" className={cn(actionBtnClass, 'no-underline')}>
                <ArrowLeft size={13} />
                <span>Back</span>
              </Link>
            </Tooltip>
          </div>
          <div className="flex-1 flex [&>span]:w-full">
            <Tooltip content="Open in Jupyter">
              <button
                onClick={handleOpenJupyter}
                disabled={isJupyterLoading}
                className={cn('group', actionBtnClass, isJupyterLoading && 'opacity-50')}
              >
                <LogoJupyter size={13} className="group-hover:hidden" />
                <Launch size={13} className="hidden group-hover:block" />
                <span>Open in Jupyter</span>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto py-1">
        {sortedChats.map((chat) => {
          const isActive = chat.id === activeChatId;
          return (
            <Link
              key={chat.id}
              href={`/chat/${chat.id}`}
              className={cn(
                'flex items-center h-8 mx-2 px-2 rounded-sm transition-colors no-underline',
                'text-[13px]',
                isActive
                  ? 'bg-muted-foreground/10 !text-foreground'
                  : '!text-foreground/70 hover:!text-foreground hover:bg-muted-foreground/10'
              )}
            >
              <span className="truncate flex-1">{chat.title || 'Untitled'}</span>
              <span className="text-[11px] text-muted-foreground/40 tabular-nums shrink-0 ml-2">
                {formatRelativeTime(chat.updatedAt)}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

interface ActiveChatLinkProps {
  chat: { id: string; title: string | null };
  isActive: boolean;
  indicator: React.ReactNode;
}

function ActiveChatLink({ chat, isActive, indicator }: ActiveChatLinkProps) {
  return (
    <div className="mx-2">
      <Link
        href={`/chat/${chat.id}`}
        className={cn(
          'group flex items-center h-8 px-2 rounded-sm transition-colors no-underline',
          'text-[13px]',
          isActive
            ? 'bg-muted-foreground/10 !text-foreground'
            : '!text-foreground/70 hover:!text-foreground hover:bg-muted-foreground/10'
        )}
      >
        <span className="truncate flex-1">{chat.title || 'Untitled'}</span>
        {indicator}
      </Link>
    </div>
  );
}

function ActiveChatsSections({ urlChatId }: { urlChatId: string | null }) {
  const { running, recentlyCompleted } = useActiveChats();
  const viewedIdsRef = useRef(new Set<string>());

  // Track viewed recently-completed chats so the dot stays hidden after navigating away
  useEffect(() => {
    if (urlChatId && recentlyCompleted.some((c) => c.id === urlChatId)) {
      viewedIdsRef.current.add(urlChatId);
    }
  }, [urlChatId, recentlyCompleted]);

  if (running.length === 0 && recentlyCompleted.length === 0) return null;

  return (
    <>
      {running.length > 0 && (
        <div>
          <SectionHeader>Running</SectionHeader>
          {running.map((chat) => (
            <ActiveChatLink
              key={chat.id}
              chat={chat}
              isActive={chat.id === urlChatId}
              indicator={
                <span className="shrink-0 ml-2 h-2 w-2 rounded-full bg-success animate-pulse" />
              }
            />
          ))}
        </div>
      )}

      {recentlyCompleted.length > 0 && (
        <div>
          <SectionHeader>Recently Completed</SectionHeader>
          {recentlyCompleted.map((chat) => (
            <ActiveChatLink
              key={chat.id}
              chat={chat}
              isActive={chat.id === urlChatId}
              indicator={
                viewedIdsRef.current.has(chat.id) ? null : (
                  <span className="shrink-0 ml-2 h-2 w-2 rounded-full bg-ds-blue-900" />
                )
              }
            />
          ))}
        </div>
      )}

      <div className="mx-3 my-2 border-t border-border/40" />
    </>
  );
}

export const SidebarConversations = memo(function SidebarConversations() {
  const { isAuthenticated } = useCurrentUser();
  const { urlWorkspaceId, urlChatId } = useSidebarData();
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);

  const {
    data: workspaces = [],
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    newlyLoadedIds,
  } = useWorkspaces();

  const { favoriteWorkspaces, favoriteIdsSet } = useFavoriteWorkspaces();

  // Find active workspace from URL (either directly or via chat)
  const activeWorkspace = useMemo(() => {
    const allWorkspaces = [...workspaces, ...favoriteWorkspaces];
    if (urlWorkspaceId) {
      return allWorkspaces.find((w) => w.id === urlWorkspaceId) ?? null;
    }
    if (urlChatId) {
      return allWorkspaces.find((w) => w.chats.some((c) => c.id === urlChatId)) ?? null;
    }
    return null;
  }, [workspaces, favoriteWorkspaces, urlWorkspaceId, urlChatId]);

  // Show focused view for workspaces with multiple chats
  const showFocusedView = activeWorkspace && activeWorkspace.chats.length > 1;

  const nonFavoriteWorkspaces = workspaces.filter((w) => !favoriteIdsSet.has(w.id));
  const groupedWorkspaces = useMemo(
    () => groupWorkspacesByTime(nonFavoriteWorkspaces),
    [nonFavoriteWorkspaces]
  );

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground/60 text-sm">
        Sign in to view workspaces
      </div>
    );
  }

  if (isLoading && workspaces.length === 0) {
    return (
      <div className="flex items-center justify-center flex-1 h-full">
        <LoadingSpinner size="2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col">
        <SectionHeader>Workspaces</SectionHeader>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-destructive">
          <Cardboard size={48} />
          <span className="text-sm">Failed to load workspaces</span>
        </div>
      </div>
    );
  }

  const createButton = (
    <button
      onClick={() => setIsNewDialogOpen(true)}
      className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-muted-foreground/60 hover:text-foreground hover:bg-muted-foreground/10 transition-colors text-xs"
      title="New workspace"
    >
      <AddLarge size={14} />
      <span>New</span>
    </button>
  );

  // Empty state
  if (workspaces.length === 0 && favoriteWorkspaces.length === 0) {
    return (
      <div className="flex-1 flex flex-col">
        <SectionHeader action={createButton}>Workspaces</SectionHeader>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground/50">
          <Cardboard size={48} />
          <span className="text-sm">No workspaces yet</span>
        </div>
        <NewWorkspaceModal open={isNewDialogOpen} onClose={() => setIsNewDialogOpen(false)} />
      </div>
    );
  }

  // Focused workspace view for multi-chat workspaces
  if (showFocusedView && activeWorkspace) {
    return <FocusedWorkspaceView workspace={activeWorkspace} activeChatId={urlChatId} />;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        {/* Running & Recently Completed */}
        <ActiveChatsSections urlChatId={urlChatId} />

        {/* Favorites */}
        {favoriteWorkspaces.length > 0 && (
          <div>
            <SectionHeader>Favorites</SectionHeader>
            {favoriteWorkspaces.map((workspace) => (
              <SidebarWorkspaceItem key={workspace.id} workspace={workspace} />
            ))}
          </div>
        )}

        {/* Workspaces */}
        <div>
          <SectionHeader action={createButton}>Workspaces</SectionHeader>

          {Array.from(groupedWorkspaces.entries()).map(([timeGroup, groupWorkspaces]) =>
            groupWorkspaces.length > 0 ? (
              <div key={timeGroup}>
                <TimeGroupHeader>{timeGroup}</TimeGroupHeader>
                {groupWorkspaces.map((workspace) => (
                  <SidebarWorkspaceItem
                    key={workspace.id}
                    workspace={workspace}
                    isNew={newlyLoadedIds.has(workspace.id)}
                  />
                ))}
              </div>
            ) : null
          )}

          {/* Load more */}
          {(hasNextPage || isFetchingNextPage) && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fetchNextPage();
              }}
              disabled={isFetchingNextPage}
              className="flex items-center justify-center gap-1.5 w-full h-8 text-xs text-muted-foreground/60 hover:text-foreground transition-colors disabled:cursor-wait"
            >
              {isFetchingNextPage ? (
                <>
                  <LoadingSpinner size="sm" />
                  Loading...
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Load more
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <NewWorkspaceModal open={isNewDialogOpen} onClose={() => setIsNewDialogOpen(false)} />
    </div>
  );
});

// ============================================================================
// MODAL
// ============================================================================

interface NewWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
}

function NewWorkspaceModal({ open, onClose }: NewWorkspaceModalProps) {
  const [name, setName] = useState('');
  const router = useRouter();
  const { invalidateConversations } = useSidebarData();

  const createWorkspaceMutation = useCreateEmptyWorkspace({ invalidateConversations });
  const createChatMutation = useCreateChatInWorkspace({ invalidateConversations });

  const isPending = createWorkspaceMutation.isPending || createChatMutation.isPending;

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleCreate = async () => {
    if (!name.trim() || isPending) return;

    try {
      const workspace = await createWorkspaceMutation.mutateAsync({
        title: name.trim(),
      });

      const chat = await createChatMutation.mutateAsync({
        workspaceId: workspace.workspaceId,
      });

      handleClose();
      router.push(`/chat/${chat.chatId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create workspace');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>New Workspace</DialogTitle>
      <DialogBody>
        <Input
          placeholder="My Analysis Project"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          disabled={isPending}
          autoFocus
        />
      </DialogBody>
      <DialogActions>
        <Button outline onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleCreate} loading={isPending} disabled={!name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
